/* eslint-disable react/no-array-index-key */

import {Box, Button, Grid, Typography} from '@mui/material';
import {isPast} from 'date-fns/esm';
import {FormikErrors} from 'formik';
import {useSnackbar} from 'notistack';
import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import AutoField, {
  ActionType,
  AutoTextField,
  DatePickerField,
} from '../../components/AutoForm/AutoFieldNew';
import {AutoFormProps} from '../../components/AutoForm/AutoFormNew';
import {FixedFooter} from '../../components/Layout/Layout';
import {LeadFormValues} from './LeadBasic';
import {useLeadContext} from './LeadFormContext';
import {useAutoForm} from '../../components/AutoForm/useAutoForm';
import {newUTCDate} from '../../utils/newUTCDate';

const address1Field: AutoTextField = {
  id: 'line1',
  label: 'Address 1 (required)',
  type: ActionType.text,
};
const address2Field: AutoTextField = {
  id: 'line2',
  label: 'Address 2',
  type: ActionType.text,
};
const address3Field: AutoTextField = {
  id: 'line3',
  label: 'Address 3',
  type: ActionType.text,
};
const townField: AutoTextField = {
  id: 'city',
  label: 'Town/City',
  type: ActionType.text,
};
const postcodeField: AutoTextField = {
  id: 'postcode',
  label: 'Postcode (required)',
  type: ActionType.text,
};
const county: AutoTextField = {
  id: 'county',
  label: 'County (required)',
  type: ActionType.text,
};
const country: AutoTextField = {
  id: 'country',
  label: 'Country',
  type: ActionType.text,
};

const moveInDateField: DatePickerField = {
  id: 'moveInDate',
  label: 'Move in date (DD/MM/YYYY)',
  type: ActionType.datepicker,
  fullWidth: true,
};

const SingleAddressForm: React.FC = () => {
  return (
    <>
      <Box>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <AutoField field={{...address1Field, id: `${address1Field.id}`}} />
          </Grid>
          <Grid item>
            <AutoField field={{...address2Field, id: `${address2Field.id}`}} />
          </Grid>
          <Grid item>
            <AutoField field={{...address3Field, id: `${address3Field.id}`}} />
          </Grid>
          <Grid item>
            <AutoField field={{...townField, id: `${townField.id}`}} />
          </Grid>
          <Grid item>
            <AutoField field={{...postcodeField, id: `${postcodeField.id}`}} />
          </Grid>
          <Grid item>
            <AutoField field={{...county, id: `${county.id}`}} />
          </Grid>
          <Grid item>
            <AutoField field={{...moveInDateField, id: `${moveInDateField.id}`}} />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

const validateAddress = (values: LeadFormValues): FormikErrors<LeadFormValues> => {
  const errors: FormikErrors<LeadFormValues> = {};
  if (!values.line1) {
    errors.line1 = 'Required';
  }
  if (!values.city) {
    errors.county = 'Required';
  }
  if (!values.postcode) {
    errors.postcode = 'Required';
  }
  if (!values.moveInDate) {
    // errors.moveInDate = 'Required';
  } else if (isPast(newUTCDate(values.moveInDate))) {
    // all good
  } else {
    errors.moveInDate = 'Must be in the past';
  }
  return errors;
};

export const LeadAddressForm: React.FC<AutoFormProps<LeadFormValues>> = () => {
  const navigate = useNavigate();
  const {goToStep, onCreateOrUpdate, saving} = useLeadContext();
  useEffect(() => {
    goToStep('address');
  }, []);
  const {closeSnackbar, enqueueSnackbar} = useSnackbar();
  const {values, setErrors} = useAutoForm<LeadFormValues>();

  return (
    <>
      <Box mt={1} mb={1} padding={3} bgcolor="white">
        <Grid container spacing={2}>
          <Grid item xs={12} md={8} lg={6}>
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <Typography variant="h6">Address:</Typography>
              </Grid>
              <SingleAddressForm />
            </Grid>
          </Grid>
        </Grid>
        <Box mt={2} />
        <FixedFooter>
          <Grid justifyContent="space-between" container>
            <Button
              onClick={() => navigate('../basic')}
              variant="contained"
              color="primary"
            >
              Back
            </Button>
            <Button
              onClick={() => {
                closeSnackbar();
                const newErrors = validateAddress(values);
                setErrors(newErrors);
                if (Object.keys(newErrors).length) {
                  enqueueSnackbar(
                    `Some fields are missing. Please complete before saving.`,
                    {
                      variant: 'error',
                      autoHideDuration: null,
                    }
                  );
                } else {
                  onCreateOrUpdate(values).then(() => {
                    navigate('/search');
                  });
                }
              }}
              variant="contained"
              color="primary"
            >
              {saving ? 'Saving...' : 'Save and exit'}
            </Button>
            <Button
              onClick={() => {
                closeSnackbar();
                const newErrors = validateAddress(values);
                setErrors(newErrors);
                if (Object.keys(newErrors).length) {
                  enqueueSnackbar(
                    `Some fields are missing. Please complete before saving.`,
                    {
                      variant: 'error',
                      autoHideDuration: null,
                    }
                  );
                } else {
                  onCreateOrUpdate(values).then((leadId) =>
                    navigate(`/leads/${leadId}/setup-account`)
                  );
                }
              }}
              variant="contained"
              color="primary"
            >
              {saving ? 'Saving...' : 'Next'}
            </Button>
          </Grid>
        </FixedFooter>
      </Box>
    </>
  );
};
